/* eslint-disable react/no-unescaped-entities */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowDownCircle, ArrowUpCircle, Plus, RefreshCw, Snowflake } from "lucide-react";
import { toast } from "sonner";
import { walletApi, gbp, fmtDate } from "@/lib/wallet";
import SEO from "@/components/site/SEO";
import CustomerBottomNav from "@/components/customer/CustomerBottomNav";

export default function CustomerWallet() {
  const [wallet, setWallet] = useState(null);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: w }, { data: t }] = await Promise.all([
        walletApi.get("/customer/wallet/me"),
        walletApi.get("/customer/wallet/transactions?limit=25"),
      ]);
      setWallet(w);
      setTxns(t.items || []);
    } catch (e) {
      toast.error("Could not load wallet");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const isFrozen = wallet?.status === "frozen";

  return (
    <div className="min-h-screen bg-brand-bgSoft text-brand-ink pb-24 md:pb-0" data-testid="customer-wallet">
      <SEO title="Wallet" path="/app/wallet" />
      <header className="bg-white border-b border-brand-line">
        <div className="container-page flex items-center justify-between h-16">
          <Link to="/app" className="inline-flex items-center gap-2 text-sm font-semibold text-brand-ink/80 hover:text-brand-ink" data-testid="wallet-back">
            <ArrowLeft className="h-4 w-4" /> Back to dashboard
          </Link>
          <button type="button" onClick={load} className="text-xs font-semibold text-brand-muted hover:text-brand-ink inline-flex items-center gap-1" data-testid="wallet-refresh">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>
      </header>

      <div className="container-page py-10 grid lg:grid-cols-3 gap-6">
        {/* Balance card */}
        <section className="lg:col-span-2 rounded-2xl bg-brand-ink text-white p-8 relative overflow-hidden" data-testid="wallet-balance-card">
          <div className="absolute inset-0 bg-grid-overlay opacity-30" />
          <div className="relative">
            <p className="label-eyebrow text-brand-green">Closed-loop wallet</p>
            <div className="mt-4 flex items-end justify-between flex-wrap gap-4">
              <div>
                <p className="font-display font-black text-5xl tracking-tight" data-testid="wallet-balance">
                  {loading ? "…" : gbp(wallet?.balance ?? 0)}
                </p>
                <p className="mt-1 text-white/60 text-sm">{wallet?.currency || "GBP"} · service credits</p>
              </div>
              <div className="flex items-center gap-2">
                {isFrozen && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/20 text-amber-300 px-3 py-1 text-xs font-semibold" data-testid="wallet-frozen-badge">
                    <Snowflake className="h-3 w-3" /> Frozen
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => nav("/app/wallet/topup")}
                  data-testid="wallet-topup-cta"
                  disabled={isFrozen}
                  className="inline-flex items-center gap-2 rounded-full bg-brand-green hover:bg-brand-greenHover disabled:opacity-50 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition"
                >
                  <Plus className="h-4 w-4" /> Top up
                </button>
              </div>
            </div>
            {isFrozen && (
              <p className="mt-4 text-xs text-amber-300/80">Your wallet is currently frozen. Contact support to restore activity.</p>
            )}
          </div>
        </section>

        {/* Quick facts */}
        <aside className="rounded-2xl border border-brand-line bg-white p-6 space-y-3 text-sm" data-testid="wallet-info-card">
          <p className="label-eyebrow">About your wallet</p>
          <ul className="space-y-2 text-brand-muted">
            <li>• GBP-only closed-loop credits</li>
            <li>• Top up via card (Stripe) anytime</li>
            <li>• Non-refundable except by support request</li>
            <li>• Used for calling, top-ups & transfers</li>
          </ul>
        </aside>

        {/* Transactions */}
        <section className="lg:col-span-3 rounded-2xl border border-brand-line bg-white p-6" data-testid="wallet-transactions">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-extrabold text-lg">Recent activity</h2>
            <span className="text-xs text-brand-muted">{txns.length} entries</span>
          </div>
          {loading ? (
            <div className="text-sm text-brand-muted">Loading…</div>
          ) : txns.length === 0 ? (
            <div className="text-sm text-brand-muted py-10 text-center" data-testid="wallet-empty">
              No transactions yet. Top up your wallet to get started.
            </div>
          ) : (
            <ul className="divide-y divide-brand-line">
              {txns.map((t) => (
                <li key={t.id} className="py-3 flex items-center gap-3" data-testid={`wallet-txn-${t.id}`}>
                  <div className={`h-9 w-9 rounded-full flex items-center justify-center ${t.direction === "credit" ? "bg-emerald-50 text-emerald-600" : t.direction === "debit" ? "bg-rose-50 text-rose-600" : "bg-slate-100 text-slate-500"}`}>
                    {t.direction === "credit" ? <ArrowDownCircle className="h-4 w-4" /> :
                     t.direction === "debit"  ? <ArrowUpCircle className="h-4 w-4" /> :
                     <Snowflake className="h-4 w-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{t.reason}</p>
                    <p className="text-xs text-brand-muted">{fmtDate(t.created_at)} · {t.ref_type}</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-bold ${t.direction === "credit" ? "text-emerald-600" : t.direction === "debit" ? "text-rose-600" : "text-brand-muted"}`}>
                      {t.direction === "credit" ? "+" : t.direction === "debit" ? "−" : ""}{gbp(t.amount)}
                    </p>
                    <p className="text-[10px] text-brand-muted">Balance: {gbp(t.balance_after)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
      <CustomerBottomNav />
    </div>
  );
}
